import { convertFeetInchToDecimalHeight, checkNull } from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { PATIENT_DETAIL_KEY } from '../../config/patientDetailsTab';
import { checkChangeRerenderReport, initialState } from '../../lib/patientDetailsTab';
import type { InformationDrawerProps } from './InformationDrawer.types';

/**
 * Holds the editable patient information form of the study's patient details drawer. Loads the
 * initial demographic state from the study data whenever the drawer title or data changes, keeps
 * that snapshot to detect report-affecting edits, and wires field changes, the ethnicity picker
 * toggle and the validated submit to handleClick.
 *
 * @param props - Study data, drawer title and the parent save callback handleClick.
 * @returns An object carrying the form state, the field change handler, the submit handler and the
 * ethnicity drawer toggle.
 */
export const useInformationDrawer = ({
  data,
  title,
  handleClick,
}: Pick<InformationDrawerProps, 'data' | 'title' | 'handleClick'>) => {
  const initDataRef = useRef<any>(undefined);
  const [state, setState] = useSetState<any>({
    errorObject: {},
    isOpenEthnicityDrawer: false,
  });

  /**
   * Writes one patient information field and drops its validation error. Merges the value under its field name and clears that field's entry in errorObject.
   *
   * @param name - Patient information field being edited.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const handleChange = (name: any, value: any) => {
    setState({
      [name]: value,
      errorObject: {
        ...state.errorObject,
        [name]: '',
      },
    });
  };

  /**
   * Validates and submits the patient information drawer's demographic edits. Rejects a height outside 1 ft to 9 ft 11 in and a weight outside 1 to 800.9 lb, requires patient id, first name, last name and date of birth through checkNull, and on success builds the patient and onboarding payload with the height converted to decimal feet and calls handleClick as a patient-information edit whose couldCheckReportSize comes from comparing the form against the initial data.
   *
   * @returns Nothing.
   */
  const onClickBottomButton = () => {
    const checkNullData = [];
    const errorObject = {};
    if (!state.heightFt || parseInt(state.heightFt, 10) < 1 || parseInt(state.heightFt, 10) > 9) {
      _.assign(errorObject, { heightFt: 'Height must be ≥ 1 ft and ≤ 9 ft 11 in' });
    }
    if (state.heightIn && parseInt(state.heightIn, 10) > 11) {
      _.assign(errorObject, { heightIn: 'Height must be ≥ 1 ft and ≤ 9 ft 11 in' });
    }
    if (!state.weight || parseFloat(state.weight) > 800.9 || parseFloat(state.weight) < 1) {
      _.assign(errorObject, { weight: 'Weight must be ≥ 1 lb and ≤ 800.9 lb' });
    }
    checkNullData.push(
      { field: 'patientId', value: state.patientId, isRequired: true },
      { field: 'firstName', value: state.firstName, isRequired: true },
      { field: 'lastName', value: state.lastName, isRequired: true },
      { field: 'dob', value: state.dob, isRequired: true },
    );
    const checkNullError = checkNull(checkNullData);
    if (!_.isEmpty(checkNullError)) {
      _.assign(errorObject, { ...checkNullError });
    }
    if (!_.isEmpty(errorObject)) {
      setState({ errorObject });
      return;
    }
    const payload = {
      patient: {
        patientId: state.patientId?.trim(),
        firstName: state.firstName?.trim(),
        middleName: state.middleName?.trim(),
        lastName: state.lastName?.trim(),
        dob: state.dob,
        gender: state.gender,
        height: convertFeetInchToDecimalHeight(state.heightFt, state.heightIn || 0),
        weight: parseFloat(state.weight),
        ethnicity: state?.ethnicity,
      },
      onboarding: {
        pacer: state?.pacer,
        icd: state?.icd,
      },
    };
    const couldCheckReportSize = checkChangeRerenderReport(state, initDataRef.current);
    // payload, status, isEditingPatientInfo = false, key = ''
    handleClick({
      payload,
      status: true,
      isEditingPatientInfo: true,
      key: PATIENT_DETAIL_KEY.PATIENT_INFORMATION,
      couldCheckReportSize,
    });
  };

  /**
   * Opens or closes the ethnicity picker of the patient information drawer. Inverts isOpenEthnicityDrawer.
   *
   * @returns Nothing.
   */
  const toggleEthnicityDrawer = () => {
    setState({ isOpenEthnicityDrawer: !state.isOpenEthnicityDrawer });
  };

  useEffect(() => {
    if (title && !_.isEmpty(data)) {
      const initData = initialState(data);
      initDataRef.current = initData;
      setState(initData);
    }
  }, [title, data]);

  return { state, handleChange, onClickBottomButton, toggleEthnicityDrawer };
};
